// Apolo IDE — Hands-free FAB + modal trigger (Sprint v11 E15 voice modal).
//
// Floating Action Button (bottom-right, always visible) that opens the
// HandsFreeOrbModal — full-duplex OpenAI Realtime (GPT-Live pacing).
// Fallback chain: STT → agent → TTS if the live session cannot start.
//
// Esc closes the modal and stops the loop.
//
// The TTS voice is user-selectable from inside the modal and persists via
// the store (code.ui.handsFreeVoice → localStorage), same pattern as the
// model picker and effort selector.

import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Headphones } from 'lucide-react';
import { useAgentStore } from '../store/useAgentStore.js';
import HandsFreeOrbModal from './HandsFreeOrbModal.jsx';

/**
 * @param {{
 *   onSubmit: (text: string) => Promise<void> | void;
 *   inline?: boolean;
 *   wakeWord?: string;
 *   className?: string;
 *   toolContext?: { root?: string, sessionId?: string };
 * }} props
 */
export default function HandsFreeButton({ onSubmit, wakeWord, className = '', toolContext, inline = false }) {
  const [open, setOpen] = useState(false);
  // Voice preference persisted in the store (localStorage). Default 'cedar'
  // is the natural Realtime voice (≈ ChatGPT Juniper). Old saved ids like
  // 'nova' are remapped by the modal.
  const voice = useAgentStore((s) => s.code.ui.handsFreeVoice) || 'cedar';
  // Creator mode: voice conducts the FULL agent (writes code, runs tools) instead
  // of the read-only conversational turn. Persisted like the voice pref; mirrored
  // to a window flag so the pure voiceTurnPolicy can read it per turn.
  const creatorMode = useAgentStore((s) => !!s.code.ui.voiceCreatorMode);
  const setUiPref = useAgentStore((s) => s.setUiPref);

  useEffect(() => {
    /** @type {*} */ (window).__apoloVoiceCreator = creatorMode;
  }, [creatorMode]);

  const handleOpen = useCallback(() => setOpen(true), []);
  const handleClose = useCallback(() => setOpen(false), []);
  const handleVoiceChange = useCallback((v) => setUiPref('handsFreeVoice', v), [setUiPref]);
  const handleCreatorChange = useCallback((v) => setUiPref('voiceCreatorMode', !!v), [setUiPref]);

  // Render the FAB through a portal to document.body so that `position: fixed`
  // is anchored to the viewport regardless of any ancestor transform/filter/
  // backdrop-filter (framer-motion, liquid-glass, etc.) — those create a new
  // containing block that otherwise traps the button inside the composer area.
  const fab = (
    <button
      type="button"
      onClick={handleOpen}
      aria-label="Abrir conversa por voz"
      title="Conversa por voz — fale com o Apolo"
      className={
        (inline ? 'rx-ctxu-btn shrink-0 ' : 'fixed bottom-6 right-6 z-[100] inline-flex h-14 w-14 items-center justify-center rounded-full ' +
        'bg-apolo-accent text-white shadow-lg shadow-apolo-accent/30 transition ' +
        'hover:bg-apolo-accentHover hover:shadow-apolo-accent/50 ' +
        'focus:outline-none focus-visible:ring-4 focus-visible:ring-apolo-accentRing ' +
        className)
      }
    >
      <Headphones size={inline ? 16 : 22} aria-hidden="true" />
      {/* Idle pulse ring — subtle "tap me" affordance */}
      <AnimatePresence>
        {!open && !inline && (
          <motion.span
            aria-hidden="true"
            className="absolute inset-0 rounded-full border-2 border-apolo-accent"
            initial={{ scale: 1, opacity: 0.6 }}
            animate={{ scale: 1.4, opacity: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.8, repeat: Infinity, ease: 'easeOut' }}
          />
        )}
      </AnimatePresence>
    </button>
  );

  return (
    <>
      {!inline && typeof document !== 'undefined' ? createPortal(fab, document.body) : fab}

      <HandsFreeOrbModal
        open={open}
        onClose={handleClose}
        onSubmit={onSubmit}
        wakeWord={wakeWord}
        voice={voice}
        onVoiceChange={handleVoiceChange}
        creatorMode={creatorMode}
        onCreatorChange={handleCreatorChange}
        toolContext={toolContext}
      />
    </>
  );
}
